import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input"];
  static values = { debounceTime: { type: Number, default: 500 } };

  connect() {
    document.getElementById("weekly-capacities-container").style.visibility =
      "hidden";

    this.pendingRequests = new Map();
    this.cachedData = this.gatherAllSummaryData();
    this.applyUpdates(this.cachedData);

    // Add focus/blur event listeners
    this.element.addEventListener("focus", this.handleFocus.bind(this), true);
    this.element.addEventListener("blur", this.handleBlur.bind(this), true);

    this.inputTargets.forEach((input) => {
      input.addEventListener("keypress", this.validateKeyPress.bind(this));
    });

    this.inputTargets.forEach((input) => {
      input.addEventListener("input", this.validateInput.bind(this));
    });

    const self = this;

    // Highlight cells based on business rules, then show the table. This
    // avoids FOUC.
    this.highlightPlannedHourCells().then(() => {
      requestAnimationFrame(() => {
        document.getElementById(
          "weekly-capacities-container",
        ).style.visibility = "visible";

        // Only focus the first input if this is the designated "first table".
        // Tried using `autofocus` attribute but it does not work :shrug:
        if (self.element.hasAttribute('data-focus')) {
          const focusableInput = this.element.querySelector('[data-focus]');
          if (focusableInput) {
            focusableInput.focus();
            focusableInput.select();
          }
        }
      });
    });
  }

  highlightPlannedHoursCell(plannedCell, capacityValue) {
    // Remove existing highlighting, if any
    const existingBackground = plannedCell.querySelector(".cell-background");
    if (existingBackground) {
      existingBackground.remove();
    }

    // Reset any previous text styling
    plannedCell.classList.remove("text-white", "fw-bold");

    const plannedHours = Math.round(parseFloat(plannedCell.textContent) || 0);

    // Create background wrapper so the <td> can be styled and not get clobbered
    // by .table-hover styling
    const wrapper = document.createElement("div");
    wrapper.className =
      "cell-background position-absolute top-0 start-0 w-100 h-100";
    wrapper.style.zIndex = "0";

    // Apply color based comparing planned vs. capacity hours:
    //
    // Under capacity = green
    // At capacity    = yellow
    // Over capacity  = red
    if (plannedHours < capacityValue) {
      wrapper.classList.add("bg-success");
      plannedCell.classList.add("text-white");
    } else if (plannedHours === capacityValue) {
      wrapper.classList.add("bg-warning");
      plannedCell.classList.add("text-white");
    } else {
      wrapper.classList.add("bg-danger");
      plannedCell.classList.add("text-white", "fw-bold");
    }

    // Set up cell for positioning if not already done
    if (plannedCell.style.position !== "relative") {
      plannedCell.style.position = "relative";
    }

    // Get existing content or create new content wrapper
    let content = plannedCell.querySelector(".position-relative.z-1");
    if (!content) {
      content = document.createElement("div");
      content.className = "position-relative z-1";
      content.innerHTML = plannedCell.innerHTML;

      // Clear cell and add new structure
      plannedCell.innerHTML = "";
      plannedCell.appendChild(wrapper);
      plannedCell.appendChild(content);
    } else {
      // Just add the new background
      plannedCell.insertBefore(wrapper, content);
    }
  }

  loadPopoverContent(event) {
    event.preventDefault(); // Prevent jumping to top of page since the tooltip is bound to an anchor tag.

    // Dismiss any other popovers
    const otherPopovers = document.querySelectorAll('[data-bs-toggle="popover"]');
    otherPopovers.forEach(el => {
      if (el !== event.currentTarget) {
        const openPopover = bootstrap.Popover.getInstance(el);
        if (openPopover) {
          openPopover.dispose();
        }
      }
    });

    const link = event.currentTarget;
    const url = link.dataset.fetchUrl;
    const popover = bootstrap.Popover.getOrCreateInstance(link,
      {
          trigger: 'click',
          html: true,
          sanitize: false
      }
    )

    fetch(url, {
      redirect: 'manual',
      headers: {
        'X-Requested-With': 'XMLHttpRequest' // Ensure Rails sees these requests as `request.xhr?`
      },
    })
      .then(response => {
       // Check if it's a redirect
       if (response.type === 'opaqueredirect') {
         window.location.href = response.url || '/signin';
         return Promise.reject('Redirecting due to session timeout / permission issue');
       }
       return response.text();
      })
      .then(html => {
        requestAnimationFrame(() => {
          popover.setContent({ '.popover-body': html });
          popover.show();
          setTimeout(() => {
            // Find the close button in the popover content
            const closeButton = document.querySelector('[data-bs-dismiss="popover"]');
            if (closeButton) {
              // Add click handler to close the popover
              closeButton.addEventListener('click', () => {
                popover.dispose();
              });
            }
          }, 0);
        })
      })
      .catch(error => {
        console.error('Error loading popover content:', error);
      });
  }

  updateHighlightForCapacityChange(event) {
    const input = event.target;
    const capacityCell = input.closest('td[data-scope="capacity-hours"]');
    if (!capacityCell) return;

    const week = capacityCell.dataset.week;
    const row = capacityCell.closest("tr");

    // Find the corresponding planned hours cell
    const plannedCell = row.querySelector(
      `td[data-week="${week}"][data-scope="planned-hours"]`,
    );
    if (!plannedCell) return;

    // Update highlighting with current input value
    const capacityValue = parseFloat(input.value) || 0;
    this.highlightPlannedHoursCell(plannedCell, capacityValue);
  }

  highlightPlannedHourCells() {
    return new Promise((resolve) => {
      const rows = this.element.querySelectorAll(
        'tr:not([data-row-type="pod-summary"]):not([data-row-type="team-summary"])',
      );

      rows.forEach((row) => {
        // Process each week cell
        for (let week = 1; week <= 4; week++) {
          const plannedCell = row.querySelector(
            `td[data-week="${week}"][data-scope="planned-hours"]`,
          );

          if (!plannedCell) continue;

          const capacityCell = row.querySelector(
            `td[data-week="${week}"][data-scope="capacity-hours"]`,
          );

          // Get capacity value
          let capacityValue = 0;
          const capacityInput = capacityCell?.querySelector("input");
          if (capacityInput) {
            capacityValue = parseFloat(capacityInput.value) || 0;
          } else {
            capacityValue = parseFloat(capacityCell?.textContent) || 0;
          }

          // Use the shared highlight function
          this.highlightPlannedHoursCell(plannedCell, capacityValue);
        }

        // Handle the total column
        const plannedSummaryCell = row.querySelector(
          'td[data-scope="planned-hours-summary"]',
        );
        const capacitySummaryCell = row.querySelector(
          'td[data-scope="capacity-hours-summary"]',
        );

        if (plannedSummaryCell && capacitySummaryCell) {
          const capacityTotal =
            parseFloat(capacitySummaryCell.textContent) || 0;
          this.highlightPlannedHoursCell(plannedSummaryCell, capacityTotal);
        }
      });

      requestAnimationFrame(() => {
        resolve();
      });
    });
  }

  // Filter form submission
  submit() {
    this.element.requestSubmit();
  }

  formatHours(num) {
    return num.toLocaleString("en-US");
  }

  validateInput(event) {
    const input = event.target;
    // Force numeric values only
    const currentValue = input.value;
    const cleanValue = currentValue.replace(/\D/g, "");

    if (currentValue !== cleanValue) {
      input.value = cleanValue;
      input.setCustomValidity("Please enter only numbers");
    } else if (parseInt(input.value) > parseInt(input.max)) {
      input.setCustomValidity(`Value must be at most ${input.max}`);
    } else {
      input.setCustomValidity(""); // Clear any validation message
    }

    input.reportValidity(); // Show validation message
  }

  validateKeyPress(event) {
    // Allow control keys like backspace
    if (
      event.key === "Backspace" ||
      event.key === "Delete" ||
      event.key === "Tab" ||
      event.key === "Escape" ||
      event.key === "Enter" ||
      event.key === "ArrowUp" ||
      event.key === "ArrowDown" ||
      event.key === "ArrowLeft" ||
      event.key === "ArrowRight"
    ) {
      return true;
    }

    // Block non-numeric keys
    if (/[0-9]/.test(event.key)) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  handleFocus(event) {
    if (event.target.tagName === "INPUT" && event.target.type === "number") {
      event.target.style.color = "";
      event.target.style.outline = "3px solid var(--ct-border-color)";
    }
  }

  handleBlur(event) {
    const input = event.target;

    if (input.tagName === "INPUT" && input.type === "number") {
      if (!input.checkValidity()) {
        input.classList.add("text-danger");
        event.preventDefault();
        return;
      } else {
        input.style.color = "";
        input.classList.remove("text-danger");
      }

      event.target.style.outline = "";
    }
  }

  calculateSummaries() {
    this.cachedData = this.gatherAllSummaryData();
    this.applyUpdates(this.cachedData);
  }

  gatherAllSummaryData() {
    const updates = {
      employees: new Map(),
      pods: new Map(),
      team: {
        weeklyPlanned: [0, 0, 0, 0],
        weeklyCapacity: [0, 0, 0, 0],
        totalPlanned: 0,
        totalCapacity: 0,
        element: this.element.querySelector("[data-row-type='team-summary']"),
      },
    };

    // Process employee rows
    this.element
      .querySelectorAll("[data-row-type='employee']")
      .forEach((row) => {
        const employeeId = row.dataset.employeeId;
        const podId = row.dataset.podId;

        // Ensure pod entry exists
        if (!updates.pods.has(podId)) {
          updates.pods.set(podId, {
            weeklyPlanned: [0, 0, 0, 0],
            weeklyCapacity: [0, 0, 0, 0],
            totalPlanned: 0,
            totalCapacity: 0,
            element: this.element.querySelector(
              `[data-row-type="pod-summary"][data-pod-id="${podId}"]`,
            ),
          });
        }

        // Calculate employee values
        let employeeData = {
          weeklyPlanned: [0, 0, 0, 0],
          weeklyCapacity: [0, 0, 0, 0],
          totalPlanned: 0,
          totalCapacity: 0,
          element: row,
        };

        // Process each week
        for (let week = 1; week <= 4; week++) {
          const plannedCell = row.querySelector(
            `[data-week="${week}"][data-scope="planned-hours"]`,
          );
          const capacityCell = row.querySelector(
            `[data-week="${week}"][data-scope="capacity-hours"]`,
          );
          const capacityInput = capacityCell?.querySelector("input");

          const plannedValue = parseInt(plannedCell?.textContent || 0);
          const capacityValue = parseInt(capacityInput?.value || 0);

          // Store initial value for later diff calculations
          capacityInput?.setAttribute("data-last-value", capacityValue);

          employeeData.weeklyPlanned[week - 1] = plannedValue;
          employeeData.weeklyCapacity[week - 1] = capacityValue;

          // Update pod totals
          if (updates.pods.has(podId)) {
            updates.pods.get(podId).weeklyPlanned[week - 1] += plannedValue;
            updates.pods.get(podId).weeklyCapacity[week - 1] += capacityValue;
          }

          // Update team totals
          updates.team.weeklyPlanned[week - 1] += plannedValue;
          updates.team.weeklyCapacity[week - 1] += capacityValue;
        }

        // Calculate row totals
        employeeData.totalPlanned = employeeData.weeklyPlanned.reduce(
          (sum, val) => sum + val,
          0,
        );
        employeeData.totalCapacity = employeeData.weeklyCapacity.reduce(
          (sum, val) => sum + val,
          0,
        );

        // Update pod and team totals
        if (updates.pods.has(podId)) {
          updates.pods.get(podId).totalPlanned += employeeData.totalPlanned;
          updates.pods.get(podId).totalCapacity += employeeData.totalCapacity;
        }
        updates.team.totalPlanned += employeeData.totalPlanned;
        updates.team.totalCapacity += employeeData.totalCapacity;

        updates.employees.set(row, employeeData);
      });

    return updates;
  }

  applyUpdates(updates) {
    window.requestAnimationFrame(() => {
      // Update employee rows
      updates.employees.forEach((data, element) => {
        const plannedSummary = element.querySelector(
          '[data-scope="planned-hours-summary"]',
        );
        const capacitySummary = element.querySelector(
          '[data-scope="capacity-hours-summary"]',
        );

        if (plannedSummary)
          plannedSummary.textContent = this.formatHours(data.totalPlanned);
        if (capacitySummary)
          capacitySummary.textContent = this.formatHours(data.totalCapacity);
      });

      // Update pod summary rows
      updates.pods.forEach((data) => {
        if (!data.element) return;

        // Update weekly cells
        for (let week = 1; week <= 4; week++) {
          const plannedCell = data.element.querySelector(
            `[data-week="${week}"][data-scope="planned-hours"]`,
          );
          const capacityCell = data.element.querySelector(
            `[data-week="${week}"][data-scope="capacity-hours"]`,
          );

          if (plannedCell)
            plannedCell.textContent = this.formatHours(
              data.weeklyPlanned[week - 1],
            );
          if (capacityCell)
            capacityCell.textContent = this.formatHours(
              data.weeklyCapacity[week - 1],
            );
        }

        // Update summary cells
        const plannedSummary = data.element.querySelector(
          '[data-scope="planned-hours-summary"]',
        );
        const capacitySummary = data.element.querySelector(
          '[data-scope="capacity-hours-summary"]',
        );

        if (plannedSummary)
          plannedSummary.textContent = this.formatHours(data.totalPlanned);
        if (capacitySummary)
          capacitySummary.textContent = this.formatHours(data.totalCapacity);
      });

      // Update team summary row
      if (updates.team.element) {
        // Update weekly cells
        for (let week = 1; week <= 4; week++) {
          const plannedCell = updates.team.element.querySelector(
            `[data-week="${week}"][data-scope="planned-hours"]`,
          );
          const capacityCell = updates.team.element.querySelector(
            `[data-week="${week}"][data-scope="capacity-hours"]`,
          );

          if (plannedCell)
            plannedCell.textContent = this.formatHours(
              updates.team.weeklyPlanned[week - 1],
            );
          if (capacityCell)
            capacityCell.textContent = this.formatHours(
              updates.team.weeklyCapacity[week - 1],
            );
        }

        // Update summary cells
        const plannedSummary = updates.team.element.querySelector(
          '[data-scope="planned-hours-summary"]',
        );
        const capacitySummary = updates.team.element.querySelector(
          '[data-scope="capacity-hours-summary"]',
        );

        if (plannedSummary)
          plannedSummary.textContent = this.formatHours(
            updates.team.totalPlanned,
          );
        if (capacitySummary)
          capacitySummary.textContent = this.formatHours(
            updates.team.totalCapacity,
          );
      }
    });
  }

  updateAffectedSummaries(row, weekNum, newValue) {
    const capacityInput = row.querySelector(
      `[data-week="${weekNum}"][data-scope="capacity-hours"] input`,
    );
    const oldValue = parseInt(
      capacityInput.getAttribute("data-last-value") || 0,
    );
    const diff = newValue - oldValue;

    if (diff === 0) return;

    const podId = row.dataset.podId;

    window.requestAnimationFrame(() => {
      // 1. Update employee row total
      const employeeCapacitySummary = row.querySelector(
        '[data-scope="capacity-hours-summary"]',
      );
      if (employeeCapacitySummary) {
        const oldTotal = parseInt(employeeCapacitySummary.textContent || 0);
        employeeCapacitySummary.textContent = oldTotal + diff;
      }

      // 2. Update week in cached data and pod summary
      if (this.cachedData.pods.has(podId)) {
        const podData = this.cachedData.pods.get(podId);

        // Update cached data
        podData.weeklyCapacity[weekNum - 1] += diff;
        podData.totalCapacity += diff;

        // Update pod UI if element exists
        if (podData.element) {
          // Update week cell
          const podWeekCell = podData.element.querySelector(
            `[data-week="${weekNum}"][data-scope="capacity-hours"]`,
          );
          if (podWeekCell) {
            podWeekCell.textContent = this.formatHours(
              podData.weeklyCapacity[weekNum - 1],
            );
          }

          // Update total
          const podTotalCell = podData.element.querySelector(
            '[data-scope="capacity-hours-summary"]',
          );
          if (podTotalCell) {
            podTotalCell.textContent = this.formatHours(podData.totalCapacity);
          }
        }
      }

      // 3. Update team totals
      this.cachedData.team.weeklyCapacity[weekNum - 1] += diff;
      this.cachedData.team.totalCapacity += diff;

      if (this.cachedData.team.element) {
        const teamWeekCell = this.cachedData.team.element.querySelector(
          `[data-week="${weekNum}"][data-scope="capacity-hours"]`,
        );
        if (teamWeekCell) {
          teamWeekCell.textContent = this.formatHours(
            this.cachedData.team.weeklyCapacity[weekNum - 1],
          );
        }

        const teamTotalCell = this.cachedData.team.element.querySelector(
          '[data-scope="capacity-hours-summary"]',
        );
        if (teamTotalCell) {
          teamTotalCell.textContent = this.formatHours(
            this.cachedData.team.totalCapacity,
          );
        }
      }

      // Update last value for future diffs
      capacityInput.setAttribute("data-last-value", newValue);
    });
  }

  async updateCapacity(event) {
    const input = event.currentTarget;

    let isValid = input.checkValidity();

    if (!isValid) {
      // input.value = input.dataset.lastValue;
      return false;
    }

    const employeeId = input.dataset.employeeId;
    const week = parseInt(input.dataset.week);
    const value = parseInt(input.value);
    const lastValue = parseInt(input.dataset.lastValue);
    const parent = input.parentElement;
    const row = input.closest("tr");
    const requestKey = `${employeeId}-${week}`;

    // Cancel any pending request
    if (this.pendingRequests.has(requestKey)) {
      clearTimeout(this.pendingRequests.get(requestKey));
    }

    // Clear existing icons
    parent
      .querySelectorAll("#status-icon, .fa-check, .fa-times, .fa-spinner")
      .forEach((el) => el.remove());

    // Set debounced request
    this.pendingRequests.set(
      requestKey,
      setTimeout(async () => {
        // Add spinner when request starts
        const icon = document.createElement("span");
        icon.id = "status-icon";
        icon.className = "fas fa-spinner fa-spin ms-2";
        parent.appendChild(icon);

        try {
          const response = await fetch(
            `/admin/reports/capacity_planning/${employeeId}`,
            {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": document.querySelector(
                  "meta[name='csrf-token']",
                ).content,
              },
              body: JSON.stringify({
                week: week,
                hours: value,
                last_value: lastValue
              }),
            },
          );

          // Update only affected summaries if successful
          if (response.ok) {
            this.updateStatusIcon(parent, 'success');
            this.updateAffectedSummaries(row, week, value);
            // Also update cell highlighting
            const plannedCell = row.querySelector(
              `td[data-week="${week}"][data-scope="planned-hours"]`,
            );
            if (plannedCell) {
              // Update highlighting with the new capacity value
              this.highlightPlannedHoursCell(plannedCell, value);
            }
          } else if (response.status === 302) {
            // Handle redirect (usually session timeout)
            window.location.href = response.headers.get('Location');
          } else if (response.status === 409) {
            this.updateStatusIcon(parent, 'conflict', 'Another user edited this value before your change. Reload the page.');
          } else {
            this.updateStatusIcon(parent, 'error', 'Invalid value.');
          }
        } catch (error) {
          console.error(error);
          this.updateStatusIcon(parent, 'unhandled', 'An unhandled error occurred.');
        }

        this.pendingRequests.delete(requestKey);
      }, this.debounceTimeValue),
    );
  }

  updateStatusIcon(parent, context = 'default', errorMessage = '') {
    // Remove any existing status icons
    parent
      .querySelectorAll(".status-icon-container, .fa-check, .fa-times, .fa-spinner, .fa-user-lock")
      .forEach((el) => el.remove());

    // Create tooltip container
    const tooltipContainer = document.createElement("span");
    tooltipContainer.className = "status-icon-container";

    // Add tooltip data attributes for error or conflict cases
    if (context !== 'success') {
      tooltipContainer.setAttribute("data-controller", "turbo-tooltip");
      tooltipContainer.setAttribute("data-placement", "top");
      tooltipContainer.setAttribute("data-turbo-tooltip-target", "tooltip");
      tooltipContainer.setAttribute("title", errorMessage);
    }

    // Create the status icon
    const statusIcon = document.createElement("span");

    if (context === 'success') {
      statusIcon.className = "fas fa-check text-success ms-2";

      // Auto-remove success icons after a delay
      setTimeout(() => {
        const container = parent.querySelector(".status-icon-container");
        if (container) container.remove();
      }, 1500);
    } else if (context === 'conflict') {
      statusIcon.className = "fas fa-user-lock text-danger ms-2";
    } else {
      statusIcon.className = "fas fa-times text-danger ms-2";
    }

    // Add icon to tooltip container and then to parent
    tooltipContainer.appendChild(statusIcon);
    parent.appendChild(tooltipContainer);
  }
}
